import { useState } from 'react';

type Validators<T> = { [K in keyof T]?: (value: T[K]) => string };

interface UseFormSettingsParams<T> {
    defaultSettings: T;
    validators?: Validators<T>;
    readOnly?: boolean;
    registerChanges?: (isChanged: boolean) => void;
}

type FormSettingsErrors<T> = Partial<Record<keyof T, string>>;

interface FormSettingsContextValue<T> {
    settings: T;
    errors: FormSettingsErrors<T>;
    onChange: <K extends keyof T>(key: K) => (value: T[K]) => void;
    setSettings: (newSettings: T) => void;
    hasErrors: boolean;
}

export function useFormSettings<T>({
    defaultSettings,
    validators = {},
    readOnly = false,
    registerChanges,
}: UseFormSettingsParams<T>): FormSettingsContextValue<T> {
    const [settings, setSettings] = useState(defaultSettings);
    const [errors, setErrors] = useState<FormSettingsErrors<T>>({});

    const onChange =
        <K extends keyof T>(key: K) =>
        (value: T[K]) => {
            if (readOnly) {
                return;
            }

            registerChanges?.(true);

            setSettings({
                ...settings,
                [key]: value,
            });

            const validator = validators[key];

            if (validator) {
                setErrors({
                    ...errors,
                    [key]: validator(value),
                });
            }
        };

    return {
        settings,
        errors,
        onChange,
        setSettings,
        hasErrors: Object.values(errors).some(Boolean),
    };
}
